// src/components/CapoOfficina/CapoOfficinaRequestForm.jsx - Form richieste per Capo Officina
import React, { useState } from 'react';
import './CapoOfficinaRequestForm.css';
import { auth } from '../../firebase';
import { createLeaveRequestForUser } from '../../firebase';

const CapoOfficinaRequestForm = ({ 
  isVisible, 
  onClose, 
  onSubmit, 
  selectedUserId, 
  selectedUserName 
}) => {
  const [requestType, setRequestType] = useState('');
  const [permissionType, setPermissionType] = useState('daily');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [timeFrom, setTimeFrom] = useState('');
  const [timeTo, setTimeTo] = useState('');
  const [protocolCode, setProtocolCode] = useState('');
  const [taxCode, setTaxCode] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reset del form
  const resetForm = () => {
    setRequestType('');
    setPermissionType('daily');
    setDateFrom('');
    setDateTo('');
    setTimeFrom('');
    setTimeTo('');
    setProtocolCode('');
    setTaxCode('');
    setNotes('');
    setError('');
  };

  // Gestisce il cambio del tipo di richiesta
  const handleRequestTypeChange = (e) => {
    setRequestType(e.target.value);
    setError('');
    setDateFrom('');
    setDateTo('');
    setTimeFrom('');
    setTimeTo('');
    setProtocolCode('');
    setTaxCode('');
    setNotes('');
  };

  // Gestisce il cambio del tipo di permesso
  const handlePermissionTypeChange = (e) => {
    setPermissionType(e.target.value);
    setDateTo('');
    setTimeFrom('');
    setTimeTo('');
    setError('');
  };

  // Calcola i giorni lavorativi tra due date
  const calculateWorkingDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    let workingDays = 0;
    
    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDays++;
      }
    }
    
    return workingDays;
  };

  // Validazione del form
  const validateForm = () => {
    setError('');
    
    if (!requestType) {
      setError('Seleziona il tipo di richiesta');
      return false;
    }
    
    if (!selectedUserId) {
      setError('Nessun meccanico selezionato');
      return false;
    }
    
    if (requestType === 'permission') {
      if (!dateFrom) {
        setError('Seleziona la data del permesso');
        return false;
      }
      
      if (permissionType === 'hourly') {
        if (!timeFrom || !timeTo) {
          setError('Seleziona l\'orario di inizio e fine per il permesso orario');
          return false;
        }
        
        if (timeFrom >= timeTo) {
          setError('L\'orario di fine deve essere successivo a quello di inizio');
          return false;
        }
      }
      
      if (permissionType === 'multi-day') {
        if (!dateTo) {
          setError('Seleziona la data di fine per il permesso multi-giorni');
          return false;
        }
        
        if (dateFrom > dateTo) {
          setError('La data di fine deve essere successiva alla data di inizio');
          return false;
        }
      }
    }
    
    if (requestType === 'vacation') {
      if (!dateFrom) {
        setError('Seleziona la data di inizio');
        return false;
      }
      
      if (!dateTo) {
        setError('Seleziona la data di fine');
        return false;
      }
      
      if (dateFrom > dateTo) {
        setError('La data di fine deve essere successiva alla data di inizio');
        return false;
      }
    }
    
    if (requestType === 'sickness') {
      if (!dateFrom) {
        setError('Seleziona la data di inizio');
        return false;
      }
      if (!protocolCode.trim()) {
        setError('Inserisci il codice di protocollo');
        return false;
      }
      if (!taxCode.trim()) {
        setError('Inserisci il codice fiscale');
        return false;
      }
      if (taxCode.length !== 16) {
        setError('Il codice fiscale deve essere di 16 caratteri');
        return false;
      }
      if (dateTo && dateFrom > dateTo) {
        setError('La data di fine deve essere successiva alla data di inizio');
        return false;
      }
    }
    
    return true;
  };

  // Invia la richiesta
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Capo Officina non autenticato');
      }
      
      // Prepara i dati della richiesta
      const requestData = {
        type: requestType,
        userId: selectedUserId // L'utente per cui si sta facendo la richiesta
      };
      
      // Aggiungi la data per permessi e ferie
      if (requestType === 'permission' || requestType === 'vacation') {
        requestData.dateFrom = dateFrom;
      }
      
      // Aggiungi campi specifici in base al tipo di richiesta
      if (requestType === 'permission') {
        requestData.permissionType = permissionType;
        
        if (permissionType === 'hourly') {
          requestData.timeFrom = timeFrom;
          requestData.timeTo = timeTo;
        } else if (permissionType === 'multi-day') {
          requestData.dateTo = dateTo;
          requestData.workingDaysCount = calculateWorkingDays(dateFrom, dateTo);
        }
      } else if (requestType === 'vacation') {
        requestData.dateTo = dateTo;
      } else if (requestType === 'sickness') {
        requestData.dateFrom = dateFrom;
        if (dateTo) {
          requestData.dateTo = dateTo;
        }
        requestData.protocolCode = protocolCode.trim();
        requestData.taxCode = taxCode.trim().toUpperCase();
      }
      
      // Aggiungi note opzionali solo per i permessi
      if (requestType === 'permission' && notes.trim()) {
        requestData.notes = notes.trim();
      }

      // Aggiungi informazioni del capo officina che sta creando la richiesta
      requestData.createdByCapoOfficina = {
        capoOfficinaId: currentUser.uid,
        capoOfficinaName: currentUser.displayName || currentUser.email,
        createdAt: new Date().toISOString()
      };
      
      console.log("CapoOfficinaRequestForm: Dati della richiesta:", requestData);
      
      // Invia la richiesta
      const result = await createLeaveRequestForUser(requestData);
      console.log("CapoOfficinaRequestForm: Risultato:", result);
      
      // Notifica il componente padre del successo
      if (onSubmit) {
        onSubmit(result);
      }
      
      // Chiudi il form
      resetForm();
      onClose();
    } catch (error) {
      console.error('CapoOfficinaRequestForm: Errore durante l\'invio della richiesta:', error);
      setError(`Si è verificato un errore: ${error.message || 'Errore sconosciuto'}. Riprova più tardi.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Se il form non è visibile, non renderizzare nulla
  if (!isVisible) {
    return null;
  }
  
  return (
    <div className="request-form-overlay">
      <div className="request-form">
        <div className="request-form-header">
          <h2>Richiesta per {selectedUserName}</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        
        {/* Banner capo officina */}
        <div style={{
          background: 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)',
          color: 'white',
          padding: '12px 15px',
          borderRadius: '8px',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '16px' }}>🔧</span>
            <strong>Stai creando una richiesta per: {selectedUserName}</strong>
          </div>
          <div style={{ fontSize: '12px', opacity: 0.9, marginTop: '4px' }}>
            La richiesta sarà registrata come creata dal Capo Officina per il meccanico
          </div>
        </div>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {requestType === 'sickness' && (
          <div className="form-legend" style={{
            backgroundColor: '#e3f2fd',
            border: '1px solid #90caf9',
            borderRadius: '6px',
            padding: '12px',
            marginBottom: '20px',
            fontSize: '0.9rem'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              marginBottom: '8px',
              color: '#1976d2',
              fontWeight: '500'
            }}>
              <span style={{ fontSize: '16px' }}>ℹ️</span>
              <strong>Informazioni richiesta malattia</strong>
            </div>
            <p style={{ margin: '0 0 8px 0', color: '#1565c0', lineHeight: '1.4' }}>
              I campi contrassegnati con <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>*</span> sono obbligatori.
            </p>
            <p style={{ margin: '0', color: '#1565c0', fontSize: '0.85rem', fontStyle: 'italic' }}>
              Il periodo di malattia può essere specificato indicando una data di fine opzionale.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Selezione tipo di richiesta */}
          <div className="form-group">
            <label htmlFor="request-type">Tipo di richiesta</label>
            <select 
              id="request-type" 
              value={requestType} 
              onChange={handleRequestTypeChange}
              required
            >
              <option value="">Seleziona tipo...</option>
              <option value="permission">Permesso</option>
              <option value="vacation">Ferie</option>
              <option value="sickness">Malattia</option>
            </select>
          </div>

          {/* Campi specifici per permesso */}
          {requestType === 'permission' && (
            <>
              <div className="form-group">
                <label htmlFor="permission-type">Tipo di permesso</label>
                <select 
                  id="permission-type" 
                  value={permissionType} 
                  onChange={handlePermissionTypeChange}
                  required
                >
                  <option value="daily">Giornaliero</option>
                  <option value="hourly">Orario</option>
                  <option value="multi-day">Multi-giorno</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="date-from">Data del permesso</label>
                <input 
                  type="date" 
                  id="date-from" 
                  value={dateFrom} 
                  onChange={(e) => setDateFrom(e.target.value)}
                  required
                />
              </div>

              {permissionType === 'hourly' && (
                <div className="time-inputs">
                  <div className="form-group">
                    <label htmlFor="time-from">Orario inizio</label>
                    <input 
                      type="time" 
                      id="time-from" 
                      value={timeFrom} 
                      onChange={(e) => setTimeFrom(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="time-to">Orario fine</label>
                    <input 
                      type="time" 
                      id="time-to" 
                      value={timeTo} 
                      onChange={(e) => setTimeTo(e.target.value)}
                      required
                    />
                  </div>
                </div>
              )}

              {permissionType === 'multi-day' && (
                <div className="form-group">
                  <label htmlFor="date-to">Data fine permesso</label>
                  <input 
                    type="date" 
                    id="date-to" 
                    value={dateTo} 
                    onChange={(e) => setDateTo(e.target.value)}
                    required
                  />
                  {dateFrom && dateTo && (
                    <small className="working-days-info">
                      Giorni lavorativi: {calculateWorkingDays(dateFrom, dateTo)}
                    </small>
                  )}
                </div>
              )}
            </>
          )}

          {/* Campi specifici per ferie */}
          {requestType === 'vacation' && (
            <>
              <div className="form-group">
                <label htmlFor="date-from">Data inizio ferie</label>
                <input 
                  type="date" 
                  id="date-from" 
                  value={dateFrom} 
                  onChange={(e) => setDateFrom(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="date-to">Data fine ferie</label>
                <input 
                  type="date" 
                  id="date-to" 
                  value={dateTo} 
                  onChange={(e) => setDateTo(e.target.value)}
                  required
                />
              </div>
            </>
          )}

          {/* Campi specifici per malattia */}
          {requestType === 'sickness' && (
            <>
              <div className="form-group">
                <label htmlFor="date-from">Data inizio malattia *</label>
                <input 
                  type="date" 
                  id="date-from" 
                  value={dateFrom} 
                  onChange={(e) => setDateFrom(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="date-to">Data fine malattia (opzionale)</label>
                <input 
                  type="date" 
                  id="date-to" 
                  value={dateTo} 
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
              <div className="sickness-fields">
                <div className="form-group">
                  <label htmlFor="protocol-code">Codice di protocollo *</label>
                  <input 
                    type="text" 
                    id="protocol-code" 
                    value={protocolCode} 
                    onChange={(e) => setProtocolCode(e.target.value)}
                    placeholder="Inserisci il codice di protocollo"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="tax-code">Codice fiscale *</label>
                  <input 
                    type="text" 
                    id="tax-code" 
                    value={taxCode} 
                    onChange={(e) => setTaxCode(e.target.value)}
                    placeholder="Inserisci il codice fiscale"
                    maxLength="16"
                    required
                  />
                </div>
              </div>
            </>
          )}
          
          {/* Campo Note opzionale - Solo per permessi */}
          {requestType === 'permission' && (
            <div className="form-group">
              <label htmlFor="request-notes">
                Note sul permesso (opzionale)
              </label>
              <textarea
                id="request-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Inserisci eventuali note o motivazioni per questo permesso..."
                rows="3"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
              <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Puoi specificare il motivo del permesso o altre informazioni utili per l'approvazione.
              </small>
            </div>
          )}
          
          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
              style={{
                backgroundColor: isSubmitting ? '#ccc' : '#6c757d',
                borderColor: isSubmitting ? '#ccc' : '#6c757d'
              }}
            >
              {isSubmitting ? 'Invio in corso...' : 'Invia richiesta'}
            </button>
            <button 
              type="button" 
              className="cancel-button" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Annulla
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CapoOfficinaRequestForm;